import React from 'react';
import { BreadcrumbSchema, FAQSchema, ArticleSchema } from '@/components/JsonLd';
import {
  RichHero, TableOfContents, ProseSection, AnswerBox, FactGrid,
  ComparisonTable, StepList, RichFAQ, RelatedLinks, RichCTA,
} from '../RichBlocks';
import SEODisclaimer from '@/components/seo/content/SEODisclaimer';
import V2Sections, { hasV2Content } from '../V2Sections';

/**
 * Generic competitor-vs-enkel rich page. Geo-aware.
 * IMPORTANT: Does NOT cite specific competitor prices (per policy: no competitor
 * electricity-seller pricing data). Instead it explains the market neutrally —
 * the physical power is identical for all suppliers; what differs is tillæg,
 * abonnement and inkluderede services — and details enkel's transparent offer.
 * Reads page.competitor_name. Registered per competitor slug + geo-variants.
 */
export default function CompetitorVsEnkel({ page }) {
  const competitor = page?.competitor_name || 'dit nuværende elselskab';
  const geo = page?.geography;
  const place = geo?.name ? ` i ${geo.name}` : '';
  const zone = geo?.zone;
  const zoneLabel = zone === 'DK1' ? 'DK1 (Vestdanmark)' : zone === 'DK2' ? 'DK2 (Østdanmark)' : null;
  const dso = zone === 'DK2' ? 'fx Radius eller Cerius' : 'fx N1 eller TREFOR El-Net';

  const updated = new Date(page?.last_data_refresh || page?.updated_date || Date.now())
    .toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });

  const v2 = hasV2Content(page);
  const faqs = (v2 && page?.faqs?.length >= 4) ? page.faqs : [
    {
      question: `Hvad er forskellen på ${competitor} og enkel energi?`,
      answer: `Den fysiske strøm er identisk – uanset leverandør leveres den via dit lokale netselskab (${dso}), og spotprisen fastsættes time for time på Nord Pool. Det der varierer mellem selskaber er tillægget til spotprisen, abonnementsprisen og hvilke services der er inkluderet. enkel energi har 0 øre tillæg til spotprisen, abonnement fra 18 kr./md. og inkluderer Saving Mode, serviceaftale og laderabat til 0 kr./md.`,
    },
    {
      question: 'Skal jeg selv opsige mit nuværende elselskab?',
      answer: `Nej. Når du tilmelder dig enkel energi, håndterer vi opsigelsen automatisk. Du skal ikke kontakte ${competitor} selv – vi klarer hele skiftet.`,
    },
    {
      question: 'Er der risiko ved at skifte elselskab?',
      answer: 'Nej. Du får den samme strøm leveret via dit lokale netselskab. Der er ingen afbrydelse og ingen nedetid – kun prisen og de inkluderede services ændrer sig. Skiftet er ofte gennemført på få dage.',
    },
    {
      question: 'Hvad koster det at skifte til enkel energi?',
      answer: 'Det koster 0 kr. at skifte. Ingen oprettelsesgebyr, ingen binding og intet depositum. Tilmelding tager omkring 5 minutter online med MitID.',
    },
    {
      question: 'Hvad får jeg ekstra som elbilist hos enkel?',
      answer: 'Som elbilist får du Saving Mode (automatisk opladning i de billigste timer), 10 øre/kWh laderabat og automatisk elafgift-refusion via serviceaftalen – alt til 0 kr./md. Det gør den reelle pris pr. kWh til hjemmeopladning markant lavere.',
    },
  ];

  const toc = [
    { id: 'svar', label: 'Kort svar' },
    { id: 'marked', label: 'Sådan fungerer markedet' },
    { id: 'enkel', label: 'enkels tilbud' },
    { id: 'skift', label: 'Sådan skifter du' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <article itemScope itemType="https://schema.org/Article">
      <BreadcrumbSchema items={[{ name: 'Forside', path: '/' }, { name: 'Sammenlign elselskaber', path: '/elpris-sammenligning' }, { name: `${competitor} vs enkel${place}`, path: `/sammenlign/${page.slug}` }]} />
      <FAQSchema faqs={faqs} />
      <ArticleSchema headline={`${competitor} vs enkel energi${place}`} description={page?.meta_description} datePublished={page?.created_date} dateModified={page?.last_data_refresh || page?.updated_date} />

      <RichHero
        eyebrow={`Sammenligning · Elselskaber${zoneLabel ? ' · ' + zone : ''} · 2026`}
        h1={(v2 && page?.h1) || `${competitor} vs enkel energi${place}: Hvad er forskellen?`}
        intro={(v2 && page?.intro_text) || `Overvejer du at skifte fra ${competitor}? Vi forklarer, hvad der faktisk adskiller elselskaber – og hvorfor gennemsigtighed, 0 øre tillæg og inkluderede elbil-fordele er kernen i enkel energi.`}
        badges={['Spotpris uden tillæg', 'Ingen binding', 'Saving Mode inkl.']}
        updated={updated}
        image="https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?auto=format&fit=crop&w=1600&q=70"
        kpis={[
          { value: '0 øre', label: 'tillæg til spotprisen' },
          { value: 'Fra 18 kr./md.', label: 'abonnement' },
          { value: '0 kr.', label: 'at skifte' },
          { value: '0 kr./md.', label: 'Saving Mode + service' },
        ]}
      />
      <TableOfContents items={toc} />

      <ProseSection id="svar" heading={`${competitor} eller enkel – kort svar`}>
        <AnswerBox question="Hvad er den reelle forskel?">
          Den <strong>fysiske strøm er identisk</strong> uanset leverandør – den leveres via dit lokale netselskab ({dso}), og spotprisen
          fastsættes time for time på Nord Pool. Forskellen ligger i <strong>tillægget til spotprisen</strong>, <strong>abonnementet</strong> og
          de <strong>inkluderede services</strong>. enkel energi har <strong>0 øre tillæg</strong>, abonnement fra 18 kr./md. og inkluderer Saving Mode,
          serviceaftale og laderabat til 0 kr./md.
        </AnswerBox>
        {zoneLabel && (
          <p>{geo.name} ligger i <strong>{zoneLabel}</strong>. Spotprisen er ens for alle leverandører i {zone} – det er vilkårene oveni, der afgør din samlede pris.</p>
        )}
      </ProseSection>

      <V2Sections page={page} startAlt />

      {!v2 && <ProseSection id="marked" heading="Sådan fungerer elmarkedet" alt>
        <p>
          Det danske elmarked er liberaliseret. Alle danske husstande kan frit vælge elleverandør, men den fysiske strøm kommer altid gennem
          dit lokale netselskab. Spotprisen sættes på elbørsen Nord Pool og er den samme for alle i samme prisområde. Derfor handler valget af
          elselskab reelt om tre ting:
        </p>
        <FactGrid columns={3} facts={[
          { value: 'Tillæg', label: 'Øre/kWh oveni spotprisen', sub: 'enkel: 0 øre' },
          { value: 'Abonnement', label: 'Fast månedlig pris', sub: 'enkel: fra 18 kr./md.' },
          { value: 'Services', label: 'Saving Mode, laderabat, service', sub: 'enkel: inkluderet' },
        ]} />
        <p className="mt-4 text-sm text-gray-500">
          Vi sammenligner ikke specifikke konkurrentpriser her. Du kan altid se uafhængige, opdaterede prissammenligninger hos
          {' '}<a href="https://stromligning.dk" target="_blank" rel="noopener noreferrer" className="underline">Strømligning.dk</a>.
        </p>
      </ProseSection>}

      <ProseSection id="enkel" heading="Det får du hos enkel energi">
        <div className="my-2">
          <ComparisonTable
            caption="enkel energis tilbud"
            columns={[{ name: 'enkel energi', highlight: true }]}
            rows={[
              { label: 'Tillæg til spotpris', values: ['0 øre/kWh'] },
              { label: 'Abonnement', values: ['Fra 18 kr./md.'] },
              { label: 'Binding', values: [false] },
              { label: 'Saving Mode', values: ['0 kr./md.'] },
              { label: 'Laderabat', values: ['10 øre/kWh'] },
              { label: 'Serviceaftale (ladeboks)', values: ['0 kr./md.'] },
              { label: 'Elafgift-refusion', values: ['Automatisk'] },
            ]}
          />
        </div>
        <p>
          For elbilister gør de inkluderede services en stor forskel: Saving Mode lader automatisk i de billigste timer, laderabatten trækker
          10 øre/kWh fra på hjemmeopladning styret af Saving Mode i enkel-appen (kompatibel ladeboks og bil kræves), og elafgiften refunderes automatisk. Tilsammen kan det dække – og overstige – dit abonnement.
        </p>
      </ProseSection>

      <ProseSection id="skift" heading="Sådan skifter du elselskab" alt>
        <StepList steps={[
          { title: 'Vælg din el-aftale', body: 'Det tager ca. 5 minutter online med MitID.' },
          { title: 'Vi opsiger det gamle', body: `Vi håndterer opsigelsen hos ${competitor} automatisk – du skal ikke gøre noget.` },
          { title: 'Aktivering', body: 'Ofte gennemført på få dage. Ingen afbrydelse i din strøm.' },
        ]} />
      </ProseSection>

      <section className="py-12 bg-[#F6F3EC]">
        <div className="container mx-auto px-6">
          <h2 id="faq" className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#1F2E33]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Ofte stillede spørgsmål</h2>
          <RichFAQ faqs={faqs} />
        </div>
      </section>

      <RelatedLinks links={[
        { path: '/elpris-sammenligning', label: 'Sammenlign elselskaber' },
        { path: '/elaftale', label: 'Se el-aftaler' },
        { path: '/pris-time-for-time', label: 'Elpris time for time' },
        { path: '/hvad-koster-el-gennem-enkel', label: 'Hvad koster el hos enkel?' },
        { path: '/portal/smart-charge', label: 'Sådan virker Saving Mode' },
        { path: '/el-fordele#laderabat', label: 'Laderabat – 10 øre/kWh' },
      ]} />

      <RichCTA
        headline="Skift til gennemsigtig el"
        subtext="Spotpris uden tillæg fra 18 kr./md., ingen binding, og Saving Mode + service inkluderet. Vi opsiger dit gamle selskab for dig."
        primary={{ path: '/elaftale', text: 'Se el-aftaler' }}
        secondary={{ path: '/elpris-sammenligning', text: 'Sammenlign elselskaber' }}
      />
      <SEODisclaimer page={page} />
    </article>
  );
}